import React, { Component, Suspense } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import ReactTable from "react-table";
import { Button, ButtonGroup, Collapse } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { Popconfirm } from "antd";

import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";
import ReactTableFilterSelect from "components/custom/forms/react-table-filter-select";
import ReactTableRangeFilter from "components/custom/forms/react-table-range-filter";
import ReactTableYesNoFilter from "components/custom/forms/react-table-yes-no-filter";
import rangeFilterMethod from "components/custom/forms/react-table-range-filter-method";
import yesNoFilterMethod from "components/custom/forms/react-table-yes-no-filter-method";
import statusFilterMethod from "components/custom/forms/react-table-status-filter-method";
import { fetchSupervisors, fetchHR } from "actions/employeeActions";

import {
    fetchApplicants,
    clearApplicants,
    deleteApplicant,
    reprocessApplicant
} from "api/menu/hr/applicants/actions";

import { fetchJobPostings } from "api/menu/hr/job-postings/actions";

import { closeCustomModal, openCustomModal } from "components/custom/modals";
import { ApplicantStatusFilterOptions, getApplicantStatusLabel } from "data/applicant-statuses";
import ApplicantsFilter from "./applicants-filter";
import UserTypes from "components/auth/user-types";
import ApplicantScoreDisplay from "./applicant-score-display";
import { getApplicantRowBackground } from "./applicant-row-style";

const ApplicantModal = React.lazy(() => import("./applicant-modal"));

const filterFullSizeStyle = { width: "100%", height: "100%" };

const ApplicantsStatusFilter = ({ filter, onChange }) => (
    <ReactTableFilterSelect
        currentValue={filter ? filter.value : ""}
        onChange={onChange}
        style={filterFullSizeStyle}
        optionsObject={ApplicantStatusFilterOptions}
        showAllOption={true}
    />
);

const ApplicantsScoreRangeFilter = props => (
    <ReactTableRangeFilter {...props} style={filterFullSizeStyle} />
);

const ApplicantsYesNoFilter = props => (
    <ReactTableYesNoFilter {...props} style={filterFullSizeStyle} />
);

class ApplicantsList extends Component {
    constructor(props) {
        super(props);
        
        // Load saved table state from localStorage
        const savedTableState = this.loadTableStateFromStorage();
        
        this.state = {
            includeInactivePostings: false,
            reprocessing: {},
            showFilters: false,
            currentFilters: {},
            summary: {
                totalApplicants: 0,
                totalWithDuplicates: 0,
                totalExEmployees: 0,
                totalRejectedAutomatically: 0,
                totalRejectedManually: 0
            },
            filtered: savedTableState?.filtered || [],
            sorted: savedTableState?.sorted || [{ id: "createdUtc", desc: true }],
            page: savedTableState?.page || 0,
            pageSize: savedTableState?.pageSize || 20
        };
    }

    componentDidMount() {
        this.props.fetchSupervisors();
        this.props.fetchHR();
        this.props.fetchJobPostings(true); // Include inactive postings to get all location data
        this.props.fetchApplicants({ includeInactivePostings: this.state.includeInactivePostings });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.applicants !== this.props.applicants) {
            this.updateSummary(this.props.applicants);
        }
    }

    componentWillUnmount() {
        this.props.clearApplicants();
    }

    getTableStateStorageKey = () => {
        // Include the environment URL to separate table state per environment
        const envKey = window.location.origin;
        return `applicantsTableState_${envKey}`;
    };

    loadTableStateFromStorage = () => {
        try {
            const storageKey = this.getTableStateStorageKey();
            const savedState = localStorage.getItem(storageKey);
            if (savedState) {
                return JSON.parse(savedState);
            }
        } catch (error) {
            console.warn('Failed to load saved table state:', error);
        }
        return null;
    };

    saveTableStateToStorage = (filtered, sorted, page, pageSize) => {
        try {
            const storageKey = this.getTableStateStorageKey();
            const tableState = { filtered, sorted, page, pageSize };
            localStorage.setItem(storageKey, JSON.stringify(tableState));
        } catch (error) {
            console.warn('Failed to save table state:', error);
        }
    };

    updateSummary = (applicants) => {
        const totalApplicants = applicants.length;
        const totalWithDuplicates = applicants.filter(a => a.hasDuplicates).length;
        const totalExEmployees = applicants.filter(a => a.isExEmployee).length;
        
        const totalRejectedAutomatically = applicants.filter(a => 
            a.status === "RejectedAutomatically"
        ).length;
        
        const totalRejectedManually = applicants.filter(a => 
            a.status === "RejectedManually"
        ).length;

        this.setState({
            summary: {
                totalApplicants,
                totalWithDuplicates,
                totalExEmployees,
                totalRejectedAutomatically,
                totalRejectedManually
            }
        });
    };

    openModal = (id = false) => {
        const modalTitle = id !== false ? "Update" : "Create";
        const modalObj = {
            id: `applicant-${id}`,
            header: `${modalTitle} Applicant`,
            modalSettings: { size: "lg" },
            children: (
                <Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <ApplicantModal
                        applicantId={id}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        onSaved={() => {
                            this.props.fetchApplicants(this.state.includeInactivePostings);
                            this.props.closeCustomModal(modalObj);
                        }}
                    />
                </Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    delete = (id, name) => {
        this.props.deleteApplicant(id, name, () => this.props.fetchApplicants(this.state.currentFilters));
    };

    handleReprocess = (id) => {
        this.setState(prevState => ({
            reprocessing: { ...prevState.reprocessing, [id]: true }
        }));
        this.props.reprocessApplicant(id, () => {
            this.setState(prevState => {
                const newReprocessing = { ...prevState.reprocessing };
                delete newReprocessing[id];
                return { reprocessing: newReprocessing };
            });
            setTimeout(() => this.props.fetchApplicants(this.state.currentFilters), 500);
        });
    };

    handleFilterSearch = (filters) => {
        this.setState({ currentFilters: filters, page: 0 });
        this.saveTableStateToStorage(this.state.filtered, this.state.sorted, 0, this.state.pageSize);
        this.props.fetchApplicants(filters);
    };

    handleFilterClose = () => {
        this.setState({ showFilters: false });
    };

    toggleFilters = () => {
        this.setState(prevState => ({ showFilters: !prevState.showFilters }));
    };

    handleFilteredChange = (filtered) => {
        this.setState(
            prevState => ({
                filtered,
                ...(prevState.page !== 0 ? { page: 0 } : {})
            }),
            () => {
                this.saveTableStateToStorage(
                    this.state.filtered,
                    this.state.sorted,
                    this.state.page,
                    this.state.pageSize
                );
            }
        );
    };

    handleSortedChange = (sorted) => {
        this.setState({ sorted, page: 0 });
        this.saveTableStateToStorage(this.state.filtered, sorted, 0, this.state.pageSize);
    };

    handlePageChange = (page) => {
        this.setState({ page });
        this.saveTableStateToStorage(this.state.filtered, this.state.sorted, page, this.state.pageSize);
    };

    handlePageSizeChange = (pageSize, page) => {
        this.setState({ pageSize, page });
        this.saveTableStateToStorage(this.state.filtered, this.state.sorted, page, pageSize);
    };

    render() {
        const { applicants, jobPostings, loading, supervisors, hr, auth } = this.props;
        const { reprocessing, showFilters, summary } = this.state;
        
        // Check if user is supervisor (restricted permissions)
        const isSupervisor = auth.user.role.includes(UserTypes.supervisor);
        
        // Create a lookup map for job postings
        const jobPostingsMap = (jobPostings || []).reduce((map, posting) => {
            map[posting.id] = posting;
            return map;
        }, {});
        const columns = [
            { Header: "ID", accessor: "id", id: "id", width: 80 },
            {
                Header: "Name",
                id: "fullName",
                accessor: original => original.fullName || `${original.firstName || ""} ${original.lastName || ""}`.trim(),
                filterMethod: (filter, row) => {
                    const value = (filter.value || "").toString().toLowerCase();
                    if (!value) return true;

                    const original = row._original || row;
                    const candidates = [
                        row.fullName,
                        original.fullName,
                        original.firstName,
                        original.lastName,
                        `${original.firstName || ""} ${original.lastName || ""}`.trim(),
                        `${original.lastName || ""}, ${original.firstName || ""}`.trim()
                    ];

                    return candidates.some(
                        candidate => candidate && candidate.toString().toLowerCase().includes(value)
                    );
                }
            },
            { Header: "Email", accessor: "email", id: "email" },
            {
                Header: "Phone",
                accessor: "phone",
                id: "phone",
                filterMethod: (filter, row) => {
                    const value = (filter.value || "").toString();
                    if (!value) return true;

                    const phone = (row.phone ?? row._original?.phone ?? "").toString();
                    const normalizedFilter = value.replace(/\D/g, "");
                    const normalizedPhone = phone.replace(/\D/g, "");

                    if (normalizedFilter) {
                        return normalizedPhone.includes(normalizedFilter);
                    }

                    return phone.toLowerCase().includes(value.toLowerCase());
                }
            },
            {
                Header: "Job Title",
                accessor: "jobTitle",
                id: "jobTitle",
                Cell: ({ original }) => {
                    const posting = jobPostingsMap[original.jobPostingId];
                    const needsAttention = posting && !posting.jobDescription;
                    return (
                        <span>
                            {original.jobTitle}
                            {needsAttention && (
                                <FontAwesomeIcon
                                    icon="exclamation"
                                    className="text-danger ml-2"
                                    title="Job posting needs attention: no description set. AI features are disabled for this applicant."
                                />
                            )}
                        </span>
                    );
                }
            },
            {
                Header: "City",
                id: "cityName",
                accessor: original => original.cityName || jobPostingsMap[original.jobPostingId]?.cityName || ""
            },
            {
                Header: "Province",
                id: "provinceName",
                accessor: original => original.provinceName || jobPostingsMap[original.jobPostingId]?.provinceName || ""
            },
            { Header: "Source", accessor: "source", id: "source" },
            { 
                Header: "Status", 
                accessor: "status", 
                id: "status", 
                Cell: ({ value }) => getApplicantStatusLabel(value),
                Filter: ApplicantsStatusFilter,
                filterMethod: statusFilterMethod
            },
            { 
                Header: "Score", 
                accessor: "finalScore", 
                id: "finalScore",
                Filter: ApplicantsScoreRangeFilter,
                filterMethod: rangeFilterMethod,
                Cell: ({ original }) => (
                    <ApplicantScoreDisplay
                        finalScore={original.finalScore}
                        scoreBreakdown={original.scoreBreakdown}
                        tooltipId={`applicant-score-${original.id}`}
                    />
                )
            },
            { 
                Header: "Ex Employee", 
                accessor: "isExEmployee", 
                id: "isExEmployee",
                Cell: ({ value }) => value ? "Yes" : "No",
                Filter: ApplicantsYesNoFilter,
                filterMethod: yesNoFilterMethod,
                width: 100
            },
            { 
                Header: "Has Duplicates", 
                accessor: "hasDuplicates", 
                id: "hasDuplicates",
                Cell: ({ value }) => value ? "Yes" : "No",
                Filter: ApplicantsYesNoFilter,
                filterMethod: yesNoFilterMethod,
                width: 120
            },
            { 
                Header: "Contact Count", 
                accessor: "contactCount", 
                id: "contactCount", 
                width: 120,
                Cell: ({ value }) => (
                    <span style={{ 
                        backgroundColor: value > 0 ? '#bbdefb' : 'transparent',
                        color: value > 0 ? '#1565c0' : 'inherit',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontWeight: value > 0 ? 'bold' : 'normal'
                    }}>
                        {value}
                    </span>
                )
            },
            { Header: "Created", id: "createdUtc", accessor: original => new Date(original.createdUtc).toLocaleString() },
            { Header: "Assigned To", accessor: "assignedToName", id: "assignedToName" },
            {
                Header: "Actions",
                id: "actions",
                className: "d-flex justify-content-center align-items-center",
                Cell: ({ original }) => (
                    <ButtonGroup>
                        <Link to={`/hr/applicants/${original.id}`} className="btn btn-sm btn-info">
                            <FontAwesomeIcon icon="user" title="View" />
                        </Link>
                        {!isSupervisor && (
                            <>
                                <Button size="sm" onClick={() => this.openModal(original.id)}>
                                    <FontAwesomeIcon icon="pen" title="Update" />
                                </Button>
                                <Button size="sm" variant="info" onClick={() => this.handleReprocess(original.id)} disabled={reprocessing[original.id]}>
                                    {reprocessing[original.id] ? <FontAwesomeIcon icon="spinner" spin /> : <FontAwesomeIcon icon="redo" title="Reprocess" />}
                                </Button>
                                <Popconfirm
                                    title={`Delete "${original.fullName || original.email || original.id}"?`}
                                    onConfirm={() => this.delete(original.id, original.fullName)}
                                    placement="bottomRight"
                                    okText="Yes"
                                    cancelText="Cancel"
                                >
                                    <Button variant="danger" size="sm">
                                        <FontAwesomeIcon icon="times" />
                                    </Button>
                                </Popconfirm>
                            </>
                        )}
                    </ButtonGroup>
                ),
                filterable: false,
                width: 150
            }
        ];

        return (
            <div className="dashboard">
                <h3 className="mb-4 d-flex align-items-center justify-content-between">
                    Applicants
                    <div className="d-flex justify-content-center flex-grow-1" style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                        <span className="text-success font-weight-bold mr-3">
                            Total: {summary.totalApplicants}
                        </span>
                        <span className="text-warning font-weight-bold mr-3">
                            Duplicates: {summary.totalWithDuplicates}
                        </span>
                        <span className="text-info font-weight-bold mr-3">
                            Ex-Employees: {summary.totalExEmployees}
                        </span>
                        <span className="text-danger font-weight-bold mr-3">
                            Rejected Auto: {summary.totalRejectedAutomatically}
                        </span>
                        <span className="text-danger font-weight-bold">
                            Rejected Manual: {summary.totalRejectedManually}
                        </span>
                    </div>
                    <div className="d-flex">
                        <Button 
                            variant="outline-primary" 
                            className="mr-2" 
                            onClick={this.toggleFilters}
                        >
                            <FontAwesomeIcon icon="filter" className="mr-1" />
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                        </Button>
                        {!isSupervisor && (
                            <Button variant="primary" onClick={() => this.openModal()}>
                                Add Applicant
                            </Button>
                        )}
                    </div>
                </h3>
                
                <Collapse in={showFilters}>
                    <div>
                        <ApplicantsFilter
                            onSearch={this.handleFilterSearch}
                            onClose={this.handleFilterClose}
                            applicants={applicants}
                            jobPostings={jobPostings}
                            supervisors={supervisors}
                            hr={hr}
                            loading={loading}
                            isSupervisor={isSupervisor}
                        />
                    </div>
                </Collapse>
                <ReactTable
                    data={applicants}
                    headerClassName="table-header"
                    columns={columns}
                    page={this.state.page}
                    pageSize={this.state.pageSize}
                    onPageChange={this.handlePageChange}
                    onPageSizeChange={this.handlePageSizeChange}
                    pageSizeOptions={[10, 20, 50, 100]}
                    resizable
                    className="-striped -highlight"
                    loading={loading}
                    filterable
                    filtered={this.state.filtered}
                    onFilteredChange={this.handleFilteredChange}
                    sorted={this.state.sorted}
                    onSortedChange={this.handleSortedChange}
                    defaultFilterMethod={filterCaseInsensitive}
                    getNoDataProps={() => ({ style: { zIndex: 0 } })}
                    getTrProps={(state, rowInfo) => {
                        if (rowInfo && rowInfo.original) {
                            const backgroundColor = getApplicantRowBackground(rowInfo.original);
                            return backgroundColor ? { style: { backgroundColor } } : {};
                        }
                        return {};
                    }}
                    style={{ height: "calc(100vh - 140px)" }}
                />
            </div>
        );
    }
}

ApplicantsList.propTypes = {
    fetchApplicants: PropTypes.func.isRequired,
    clearApplicants: PropTypes.func.isRequired,
    deleteApplicant: PropTypes.func.isRequired,
    reprocessApplicant: PropTypes.func.isRequired,
    fetchSupervisors: PropTypes.func.isRequired,
    fetchHR: PropTypes.func.isRequired,
    fetchJobPostings: PropTypes.func.isRequired,
    applicants: PropTypes.array.isRequired,
    jobPostings: PropTypes.array.isRequired,
    supervisors: PropTypes.array.isRequired,
    hr: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    auth: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
    applicants: state.applicants.applicants,
    jobPostings: state.jobPostings.jobPostings,
    supervisors: state.employee.supervisors,
    hr: state.employee.hr,
    loading: state.applicants.loading,
    auth: state.auth
});

const mapDispatchToProps = dispatch => ({
    fetchApplicants: bindActionCreators(fetchApplicants, dispatch),
    clearApplicants: bindActionCreators(clearApplicants, dispatch),
    deleteApplicant: bindActionCreators(deleteApplicant, dispatch),
    reprocessApplicant: bindActionCreators(reprocessApplicant, dispatch),
    fetchJobPostings: bindActionCreators(fetchJobPostings, dispatch),
    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch),
    fetchSupervisors: bindActionCreators(fetchSupervisors, dispatch),
    fetchHR: bindActionCreators(fetchHR, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(ApplicantsList);
